import { useEffect, useMemo } from 'react'
import { usePanelHistoryTop } from 'weblite-router'
import { Divider } from '@mui/material'
// Common
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { WChannelIcon, WGroupIcon } from '@weblite/icons'
import { AddUserIcon } from '$common/icons'
import { MenuIcon } from '$common/icons/menu'
import { EmptyStateList } from '$common/empty-state-list'
import {
  UserListItemSkeleton,
  UserListItemWithStatus,
} from '$common/user-list-item'
import { withSearchableListPage } from '$common/searchable-list-page'
import {
  closeContacts,
  openAddContact,
  openCreateHall,
  openSelectWorkspaceName,
} from '$logic/router'
import { showContactMenu } from '$comp/modals/menus/contact-menu'
import {
  useSearchedSignedUpContactIds,
  useSignedUpContactIds,
} from '$logic/contacts/react-hook'
import { SignedUpContact } from '$logic/contacts/types'
import { t } from '$i18n'
import { getPvByUserId } from '$logic/pv'
import { openRoom } from '$logic/opened-room'
import { sendSyncContactsEventToTwa } from '$logic/twaCommunication'
import { SyncIOSContactsFab } from '$comp/_app/components/leftPanel/components/contacts/sync-ios-contacts-fab'
import { IS_IOS, IS_SAFARI } from '$func/environmentInfo'
import { CommonOnboarding } from '$logic/onboarding/types'
import { Groups } from '@mui/icons-material'
import { useIsDefaultWorkspace } from '$logic/workspace'
import { useIsVIPPlanEnabledForUser } from '$logic/premium-features/react-hooks'
import {
  PremiumFeatureType,
  showBuyPremiumFeatureDialog,
} from '$comp/modals/buy-premium-feature-dialog'

const onSendMessage = async (userId: string) => {
  const { roomId } = (await getPvByUserId(userId)) || {}
  if (roomId) openRoom(roomId)
}

function ListItem({ id }: { id: string }) {
  return (
    <>
      <UserListItemWithStatus
        userId={id}
        hasDivider={false}
        onClick={() => onSendMessage(id)}
        SecondaryActionIcon={MenuIcon}
        onSecondaryActionIconClick={() => showContactMenu({ userId: id })}
      />
      <Divider variant="inset" />
    </>
  )
}

const onCreateChannel = async () => {
  await closeCurrentModal()
  await openCreateHall('CHANNEL', undefined)
}

const onCreateGroup = async () => {
  await closeCurrentModal()
  await openCreateHall('GROUP')
}

export function Contacts() {
  const isVIPPlanEnabled = useIsVIPPlanEnabledForUser()
  const panelTop = usePanelHistoryTop('profile')
  useEffect(() => {
    const [topPage] = panelTop?.location?.split('/').slice(-1) ?? []
    if (topPage === 'CONTACTS') sendSyncContactsEventToTwa()
  }, [panelTop])

  const isCurrentWorkSpaceDefault = useIsDefaultWorkspace()

  const onCreateWorkspace = () => {
    if (isVIPPlanEnabled) openSelectWorkspaceName('')
    else
      showBuyPremiumFeatureDialog({ featureType: PremiumFeatureType.WORKSPACE })
  }

  const Page = useMemo(
    () =>
      withSearchableListPage<SignedUpContact, null, string>({
        appBarTitle: t('myAccount.contact'),
        ListItem,
        SkeletonItem: UserListItemSkeleton,
        EmptyStateComponent: () => <EmptyStateList />,
        fetchHook: useSignedUpContactIds,
        searchHook: useSearchedSignedUpContactIds,
        actionButtons: [
          {
            text: t('newChatModal.createChannel'),
            icon: <WChannelIcon sx={{ color: 'secondary500' }} />,
            onClick: onCreateChannel,
            onboardingType: CommonOnboarding.CREATE_CHANNEL_BUTTON,
          },
          {
            text: t('newChatModal.createGroup'),
            icon: <WGroupIcon sx={{ color: 'secondary500' }} />,
            onClick: onCreateGroup,
          },
          {
            text: t('contacts.add'),
            icon: <AddUserIcon sx={{ color: 'secondary500' }} />,
            onClick: openAddContact,
          },
        ],
        secondaryActionButtons: isCurrentWorkSpaceDefault
          ? [
              {
                text: t('selectWorkspaceName.title'),
                icon: <Groups sx={{ color: 'secondary500' }} />,
                onClick: onCreateWorkspace,
              },
            ]
          : [],
        onBack: closeContacts,
        FAB: IS_IOS && IS_SAFARI ? SyncIOSContactsFab : undefined,
      }),
    [isCurrentWorkSpaceDefault],
  )

  return <Page fetchHookParams={null} getSearchHookParams={query => query} />
}
